import {
  AppShell,
  Button,
  Loader,
  TextInput,
  Text,
  Group,
  Modal,
  Table,
  Skeleton,
  Textarea,
  Paper,
  Stack,
  Switch,
  MultiSelect,
  Fieldset,
  Anchor,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { reset, initialize, updateField } from "../slices/form/guestTab";
import { useGetVentQuery } from "../slices/api/ventsApi";
import {
  DeleteAssignmentRequest,
  useDeleteAssignmentMutation,
  useGetAssignmentsQuery,
} from "../slices/api/assignmentsApi";
import {
  DeleteGuestRequest,
  SaveGuestRequest,
  useDeleteGuestMutation,
  useGetGuestQuery,
  useSaveGuestMutation,
} from "../slices/api/guestsApi";
import { JSX, useEffect, useState } from "react";
import {
  displayErrorMessage,
  displayInfoMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";

import { IsBlockNav, SetBlockNav } from "../App";
import {
  formatFullName,
  formatPrimaryName,
  getActivityAssignmentsText,
  isValidEmail,
} from "../shared/utility";
import { useDisclosure } from "@mantine/hooks";
import { navigate } from "wouter/use-browser-location";
import CreateGuest from "./CreateGuest";
import { GuestStatusSegmentedControl } from "../shared/components/GuestsStatusSegmentedControl";
import {
  formatGuestStatus,
  GetAssignment,
  GetAssignmentsList,
  GetTotalForActivity,
} from "../slices/types";
import { openConfirmationModal } from "../shared/components/Notifications";
import { Link } from "wouter";
import UpsertAssignment from "./UpsertAssignment";
import ActivityAssignments from "./ActivityAssignments";
import {
  IconAddNew,
  IconDelete,
  IconEdit,
  IconGuests,
  IconHelp,
  IconRsvp,
  IconSendMessage,
} from "../shared/components/Icons";
import { IconCheck } from "@tabler/icons-react";
import { useAuth } from "../authentication/AuthProvider";
import { LabelWithTooltipHelp } from "../shared/components/LabelWithTooltipHelp";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { setConsoleIds } from "./Console";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import SendGuestMessage from "./SendGuestMessage";
import SendHostMessage from "./SendHostMessage";
import { getPersonalVentLink } from "../shared/links";

// We could get the ventId from the guest doc, but this way we won't need to wait
interface Props {
  ventId: string;
  guestId: string;
  setTabDescription: (name: string) => void;
  setEventTabDescription: (name: string) => void;
}

const GuestTab = (props: Props) => {
  const { ventId, guestId } = props;
  const authContext = useAuth();
  const {
    data: guest,
    hasChanges,
    initialData,
  } = useAppSelector((state: RootState) => state.guestTab);
  const {
    data: guestData,
    isFetching: isFetchingGuest,
    isSuccess: isSuccessGuest,
    isError: isErrorFetchingGuest,
    error: guestError,
  } = useGetGuestQuery({
    id: guestId,
  });

  const {
    data: ventResponse,
    isFetching: isFetchingVent,
    isSuccess: isSuccessVent,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: ventId,
  });
  const {
    data: assignmentsResponse,
    isFetching: isFetchingAssignments,
    isSuccess: isSuccessAssignments,
  } = useGetAssignmentsQuery({
    ventId,
  });

  const dispatch = useAppDispatch();
  const [deleteGuest] = useDeleteGuestMutation();
  useEffect(() => {
    if (guestData) {
      dispatch(initialize(guestData));
      props.setTabDescription(
        formatFullName(guestData.firstName, guestData.lastName),
      );
    }
  }, [guestData]);
  useEffect(() => {
    if (ventResponse) {
      props.setEventTabDescription(ventResponse.name);
    }
  }, [ventResponse]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveGuest, { isLoading: isSavingGuest }] = useSaveGuestMutation();
  const [deleteAssignment] = useDeleteAssignmentMutation();
  const [openedPlusOne, { open: openPlusOne, close: closePlusOne }] =
    useDisclosure(false);
  const [openedAssignment, { open: openAssignment, close: closeAssignment }] =
    useDisclosure(false);
  const [
    openedSendGuestMessage,
    { open: openSendGuestMessage, close: closeSendGuestMessage },
  ] = useDisclosure(false);
  const [
    openedSendHostMessage,
    { open: openSendHostMessage, close: closeSendHostMessage },
  ] = useDisclosure(false);
  const [
    openedActivityAssignments,
    { open: openActivityAssignments, close: closeActivityAssignments },
  ] = useDisclosure(false);
  const [currentActivitySummary, setCurrentActivitySummary] = useState(
    {} as ActivitySummary,
  );

  const [sendMessageGuestId, setSendMessageGuestId] = useState<string>("");

  const handleDelete = async (guestId: string, ventId: string) => {
    if (guestId === undefined) {
      console.error("guestId is undefined but should not be");
      return;
    }

    const request: DeleteGuestRequest = {
      id: guestId,
      ventId,
    };

    try {
      await deleteGuest(request).unwrap();
      setConsoleIds({ guestId: null });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleSave = async () => {
    if (guest === undefined) {
      console.error("guest is undefined but should not be");
      return;
    }
    if (ventResponse === undefined) {
      console.error("vent is undefined but should not be");
      return;
    }
    if (guest.firstName == null) {
      displayErrorMessage("First name is required.");
      return;
    }
    if (
      (guest.email == null || guest.email === "") &&
      guest.primaryGuestId === ""
    ) {
      displayErrorMessage("Email is required.");
      return;
    }

    if (
      guest.email != null &&
      guest.email !== "" &&
      !isValidEmail(guest.email)
    ) {
      displayErrorMessage("Email is not valid.");
      return;
    }

    const request: SaveGuestRequest = {
      id: guestId,
      ventId: guest.ventId,
      primaryGuestId: guest.primaryGuestId,
      email: guest.email,
      firstName: guest.firstName!,
      lastName: guest.lastName,
      status: guest.status,
      isUnsubscribed: guest.isUnsubscribed,
      blockedGuestMessaging: guest.blockedGuestMessaging,
      customFieldValues: guest.customFieldValues,
      hostNote: guest.hostNote,
      familyGreeting: guest.familyGreeting,
      updateTime: guest.updateTime!,
    };

    try {
      await saveGuest(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleDeleteAssignment = async (activityId: string) => {
    if (guest === undefined) {
      console.error("guest is undefined but should not be");
      return;
    }

    const request: DeleteAssignmentRequest = {
      ventId: guest.ventId,
      activityId: activityId,
      guestId: guest.id,
    };

    try {
      await deleteAssignment(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingGuest || isErrorFetchingVent) {
    handleFetchingError(guestError ?? ventError);
    return null;
  }

  if (
    isFetchingGuest ||
    !guestData ||
    !guest ||
    isFetchingVent ||
    !ventResponse
  ) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  <div hidden={guest.primaryGuestId === ""}>
    <Paper mt="xs">
      <Anchor
        href={`/console/guest/event/${ventId}/guest/${guest.primaryGuestId}`}
      >
        Invited by{" "}
        {formatPrimaryName(
          guest.primaryGuestId,
          guest.primaryFirstName,
          guest.primaryLastName,
        )}
      </Anchor>
    </Paper>
  </div>;

  const hasUpdatePermission = guest.canUserEdit;

  let plusOneRows: JSX.Element[] = [];
  const guestHasPlusOnes =
    guestData?.plusOnes != null && guestData.plusOnes.length > 0;
  if (guestHasPlusOnes) {
    // Copy because immutable
    const sorted = [...guestData.plusOnes].sort((a, b) =>
      (a.lastName ?? "") < (b.lastName ?? "") ? 1 : -1,
    );
    plusOneRows = sorted.map((plusOneGuest) => (
      <Table.Tr key={plusOneGuest.id}>
        <Table.Td>
          <Group gap="0.5rem">
            <EbActionIcon
              disabled={hasChanges}
              aria-label="Edit plus one"
              onClick={() => {
                navigate(
                  `/console/guest/event/${ventId}/guest/${plusOneGuest.id}`,
                );
              }}
            >
              <EbTooltip label="Edit plus one">
                <IconRsvp />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              disabled={hasChanges || !hasUpdatePermission}
              aria-label="Delete guest"
              onClick={() => {
                openConfirmationModal({
                  message: `Please confirm deletion of the plus one ${formatFullName(plusOneGuest.firstName, plusOneGuest.lastName)}`,
                  title: "Delete Plus One",
                  onConfirm: () => handleDelete(plusOneGuest.id!, guest.ventId),
                });
              }}
            >
              <EbTooltip label="Delete plus one">
                <IconDelete />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>{formatGuestStatus(plusOneGuest.status)}</Table.Td>
        <Table.Td>{plusOneGuest.firstName}</Table.Td>
        <Table.Td>{plusOneGuest.lastName}</Table.Td>
        <Table.Td>{plusOneGuest.email}</Table.Td>
        <Table.Td>
          {getActivityAssignmentsText(
            assignmentsResponse?.assignments,
            plusOneGuest.id,
            ventResponse.activities,
          )}
        </Table.Td>
      </Table.Tr>
    ));
  }

  interface ActivitySummary {
    activityId: string;
    activityDescription: string;
    activityLimitPerGuest: number;
    activityLimitPerEvent: number;
    hostManaged: boolean;
    assignmentQuantity: number;
    assignmentComment: string;
    totalAssignmentQuantity: number;
    otherAssignments: {
      guestId: string;
      quantity: number;
      comment: string;
    }[];
  }

  let assignmentRows: JSX.Element[] = [];
  const allAssignments = assignmentsResponse?.assignments;
  let guestHasAssignments = false;
  if (isSuccessVent && isSuccessAssignments && isSuccessGuest) {
    // We get all assignments and all event activities.
    // Need one row per event activity, outer joining the assignments for the guest,
    // and outer joining total for all guests.
    const activitySummaries: Array<ActivitySummary> = [];
    for (const activityId of Object.keys(ventResponse.activities ?? {})) {
      const activity = ventResponse.activities[activityId];
      const guestAssignment = GetAssignment(
        allAssignments,
        guestId,
        activityId,
      );
      if (guestAssignment.quantity > 0) guestHasAssignments = true;
      const activitySummary: ActivitySummary = {
        activityId,
        activityDescription: activity.description,
        activityLimitPerGuest: activity.limitPerGuest,
        activityLimitPerEvent: activity.limitPerEvent,
        hostManaged: activity.hostManaged,
        assignmentQuantity: guestAssignment.quantity,
        assignmentComment: guestAssignment.comment,
        totalAssignmentQuantity: GetTotalForActivity(
          allAssignments,
          activityId,
        ),
        otherAssignments: GetAssignmentsList(allAssignments).filter(
          (v) => v.guestId != guestId,
        ),
      };
      activitySummaries.push(activitySummary);
    }

    const sorted = activitySummaries.sort((a, b) => {
      if (a.assignmentQuantity === 0 && b.assignmentQuantity > 0) return 1;
      if (a.assignmentQuantity > 0 && b.assignmentQuantity === 0) return -1;
      if (a.activityDescription === b.activityDescription) return 0;
      return a.activityDescription < b.activityDescription ? -1 : 1;
    });
    assignmentRows = sorted.map((activitySummary) => (
      <Table.Tr key={activitySummary.activityId}>
        <Table.Td>
          <Group gap="0.5rem">
            <EbTooltip
              label={
                activitySummary.assignmentQuantity > 0
                  ? "Edit assignment"
                  : "Add assignment"
              }
            >
              <EbActionIcon
                disabled={!hasUpdatePermission}
                aria-label={
                  activitySummary.assignmentQuantity > 0
                    ? "Edit assignment"
                    : "Add assignment"
                }
                onClick={() => {
                  if (hasChanges) {
                    displayInfoMessage(
                      "You have unsaved RSVP changes. Save or discard them before modifying an assignment.",
                    );
                    return;
                  }
                  setCurrentActivitySummary(activitySummary);
                  openAssignment();
                }}
              >
                {activitySummary.assignmentQuantity > 0 ? (
                  <IconEdit />
                ) : (
                  <IconAddNew />
                )}
              </EbActionIcon>
            </EbTooltip>{" "}
            {ventResponse.canUserViewGuests && (
              <EbActionIcon
                style={{
                  display:
                    activitySummary.totalAssignmentQuantity > 0
                      ? "block"
                      : "none",
                }}
                aria-label="See who is assigned"
                onClick={() => {
                  if (hasChanges) {
                    displayInfoMessage(
                      "You have unsaved RSVP changes. Save or discard them before seeing who is assigned.",
                    );
                    return;
                  }
                  setCurrentActivitySummary(activitySummary);
                  openActivityAssignments();
                }}
              >
                <EbTooltip label="See who is assigned">
                  <IconGuests />
                </EbTooltip>
              </EbActionIcon>
            )}
            <EbActionIcon
              disabled={!hasUpdatePermission}
              style={{
                display:
                  activitySummary.assignmentQuantity > 0 ? "block" : "none",
              }}
              aria-label="Remove assignment"
              onClick={() => {
                if (hasChanges) {
                  displayInfoMessage(
                    "You have unsaved RSVP changes. Save or discard them before removing an assignment.",
                  );
                  return;
                }
                openConfirmationModal({
                  message: `Please confirm removal of the assignment to the activity ${activitySummary.activityDescription}`,
                  title: "Remove assignment",
                  onConfirm: () =>
                    handleDeleteAssignment(activitySummary.activityId),
                });
              }}
            >
              <EbTooltip label="Remove assignment">
                <IconDelete />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>
          <Group gap={0}>
            <IconCheck
              size={16}
              style={{
                display:
                  activitySummary.assignmentQuantity > 0 ? "block" : "none",
              }}
            />
            <Text
              style={{ marginLeft: 0 }}
              c={activitySummary.assignmentQuantity > 0 ? "green" : "inherit"}
              fw={activitySummary.assignmentQuantity > 0 ? "bold" : "inherit"}
            >
              {activitySummary.activityDescription +
                (activitySummary.hostManaged ? " (host managed)" : "")}
            </Text>
          </Group>
        </Table.Td>
        <Table.Td>{activitySummary.assignmentComment}</Table.Td>
        <Table.Td>{activitySummary.assignmentQuantity}</Table.Td>
        {activitySummary.activityLimitPerEvent > 0 && (
          <Table.Td>
            {activitySummary.activityLimitPerEvent -
              activitySummary.totalAssignmentQuantity}{" "}
            of {activitySummary.activityLimitPerEvent}
          </Table.Td>
        )}
        {activitySummary.activityLimitPerEvent === 0 && (
          <Table.Td>Have {activitySummary.totalAssignmentQuantity}</Table.Td>
        )}
      </Table.Tr>
    ));
  }

  let customFieldElements: JSX.Element[] = [];
  if (ventResponse.customFields && ventResponse.customFields.length > 0) {
    customFieldElements = ventResponse.customFields.map((customField) => {
      // If the field is hidden, don't show it unless it has data.
      if (
        customField.hidden &&
        (guest.customFieldValues == null ||
          guest.customFieldValues[customField.id].length === 0)
      ) {
        return <span key={customField.id}></span>;
      }
      if (customField.fieldType === "t") {
        const values = guest.customFieldValues
          ? (guest.customFieldValues[customField.id] ?? [])
          : [];
        let value = "";
        if (values) {
          value = values[0]; // Text field only has one value
        }
        return (
          <TextInput
            key={customField.id}
            maxLength={100}
            disabled={!hasUpdatePermission}
            mt="md"
            label={customField.name}
            description={customField.description}
            required={customField.configText!.required}
            value={value ?? ""}
            onChange={(event) => {
              const newCustomFieldValues = { ...guest.customFieldValues };
              newCustomFieldValues[customField.id] =
                event.currentTarget.value === ""
                  ? []
                  : [event.currentTarget.value];
              dispatch(
                updateField({
                  field: "customFieldValues",
                  value: newCustomFieldValues,
                }),
              );
            }}
          />
        );
      } else if (customField.fieldType === "o") {
        const currentValues = guest.customFieldValues
          ? (guest.customFieldValues[customField.id] ?? [])
          : [];
        // Merge the current values with the options defined for the field
        // and remove duplicates. List the defined options first, then any additional current values.
        const optionValues = [...customField.configOptions!.options];
        currentValues.forEach((value) => {
          if (!optionValues.includes(value)) {
            optionValues.push(value);
          }
        });
        const maxOptions =
          (customField.configOptions?.maxOptions ?? 0) === 0
            ? undefined
            : customField.configOptions?.maxOptions;
        const minOptions = customField.configOptions?.minOptions ?? 0;

        let placeholder: string;
        if (maxOptions) {
          if (maxOptions === minOptions) placeholder = `Select ${maxOptions}`;
          else {
            placeholder = `Select between ${minOptions} and ${maxOptions}`;
          }
        } else if (optionValues.length > 1) {
          placeholder = `Select ${minOptions} or more`;
        } else {
          placeholder = `Select ${minOptions}`;
        }
        return (
          <MultiSelect
            key={customField.id}
            disabled={!hasUpdatePermission}
            mt="md"
            label={customField.name}
            maxValues={maxOptions}
            placeholder={placeholder}
            description={customField.description}
            required={minOptions > 0}
            data={optionValues}
            value={currentValues}
            onChange={(newValues) => {
              const newCustomFieldValues = { ...guest.customFieldValues };
              newCustomFieldValues[customField.id] = newValues ?? [];
              dispatch(
                updateField({
                  field: "customFieldValues",
                  value: newCustomFieldValues,
                }),
              );
            }}
          />
        );
      } else {
        return <span key={customField.id}></span>;
      }
    });
  }

  return (
    <>
      <AppShell.Main>
        <Modal
          opened={openedPlusOne}
          onClose={closePlusOne}
          withCloseButton={false}
          centered
        >
          <CreateGuest
            title="Add Plus One"
            onClose={closePlusOne}
            emailRequired={false}
            ventId={ventId}
            defaultStatus={"u"}
            primaryGuestId={guestId}
            primaryGuestFirstName={guestData.firstName}
            primaryGuestLastName={guestData.lastName}
          />
        </Modal>
        <Modal
          opened={openedAssignment}
          onClose={closeAssignment}
          withCloseButton={false}
          centered
        >
          <UpsertAssignment
            title={`Edit assignment for: ${currentActivitySummary.activityDescription}`}
            onClose={closeAssignment}
            ventId={ventId}
            guestId={guestId}
            activityId={currentActivitySummary.activityId}
            limitPerGuest={currentActivitySummary.activityLimitPerGuest}
            limitPerEvent={currentActivitySummary.activityLimitPerEvent}
            assignmentQuantity={currentActivitySummary.assignmentQuantity}
            assignmentComment={currentActivitySummary.assignmentComment}
            othersAssignmentQuantity={
              currentActivitySummary.totalAssignmentQuantity -
              currentActivitySummary.assignmentQuantity
            }
          />
        </Modal>
        <Modal
          size="auto"
          opened={openedActivityAssignments}
          onClose={closeActivityAssignments}
          withCloseButton={false}
          centered
        >
          <ActivityAssignments
            ventId={ventId}
            activityId={currentActivitySummary.activityId}
            onClose={closeActivityAssignments}
          />
        </Modal>
        <Modal
          opened={openedSendGuestMessage}
          onClose={closeSendGuestMessage}
          withCloseButton={false}
          centered
        >
          <SendGuestMessage
            isSenderHost={ventResponse.canUserViewConfig}
            onClose={closeSendGuestMessage}
            guestId={sendMessageGuestId}
          />
        </Modal>
        <Modal
          opened={openedSendHostMessage}
          onClose={closeSendHostMessage}
          withCloseButton={false}
          centered
        >
          <SendHostMessage onClose={closeSendHostMessage} ventId={ventId} />
        </Modal>
        <Group justify="space-between" mb="xs" gap="0">
          <Stack gap="0">
            <Text mb="0" size="xl">
              RSVP for:{" "}
              {ventResponse.canUserViewConfig && (
                <EbActionIcon
                  ml="sm"
                  mr="sm"
                  aria-label="Send message to guest"
                  onClick={() => {
                    setSendMessageGuestId(guest.id);
                    openSendGuestMessage();
                  }}
                >
                  <EbTooltip label="Send message to guest">
                    <IconSendMessage />
                  </EbTooltip>
                </EbActionIcon>
              )}
              {formatFullName(guest.firstName, guest.lastName)}{" "}
              {guest.primaryGuestId !== "" && (
                <Anchor
                  component={Link}
                  href={`/console/guest/event/${ventId}/guest/${guest.primaryGuestId}`}
                >
                  Invited by{" "}
                  {formatPrimaryName(
                    guest.primaryGuestId,
                    guest.primaryFirstName,
                    guest.primaryLastName,
                  )}
                </Anchor>
              )}
            </Text>
            <Group>
              <Text size="sm" c="dimmed">
                Event home page:{" "}
              </Text>
              <LinkAnchorToVentHome ventId={ventId} size="sm">
                {ventResponse.name}
              </LinkAnchorToVentHome>
              <Text size="sm" c="dimmed">
                (Tip: download your calendar invitation from the home page with
                your assignments and plus ones, if any.)
              </Text>
            </Group>
            {ventResponse.canUserViewConfig && guest.primaryGuestId == "" && (
              <Group>
                <span>
                  <Text size="sm" c="dimmed">
                    Personal event link for guest:{" "}
                    <Anchor
                      size={"sm"}
                      href={getPersonalVentLink(guest.guid)}
                      target="_blank"
                    >
                      {window.location.origin + getPersonalVentLink(guest.guid)}
                    </Anchor>
                    <EbTooltip label="The guest can RSVP faster using this link, because it simplifies the process of registering with Early Bird. If you click it, you will be taken to the event home page because you are already logged in.">
                      <IconHelp />
                    </EbTooltip>
                  </Text>
                </span>
              </Group>
            )}
            <Button
              mt="xs"
              mr="xl"
              onClick={() => {
                if (hasChanges) {
                  displayInfoMessage(
                    "You have unsaved RSVP changes. Save or discard them before sending a message to the host.",
                  );
                  return;
                }
                openSendHostMessage();
              }}
              disabled={isSavingGuest}
            >
              Send Message to Host
            </Button>
            <Textarea
              disabled={true}
              mt="md"
              label="Messages Sent to Host"
              value={guest.messages ?? ""}
            />
          </Stack>
        </Group>
        <Fieldset legend="RSVP">
          <Group mt="0" mb="sm">
            <TextInput
              disabled={!hasUpdatePermission}
              style={{ width: "150px" }}
              label="First Name"
              required
              value={guest.firstName ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "firstName",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />

            <TextInput
              disabled={!hasUpdatePermission}
              style={{ width: "150px" }}
              label="Last Name"
              value={guest.lastName ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "lastName",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
            {guest.primaryGuestId === "" && (
              <TextInput
                disabled={
                  !hasUpdatePermission ||
                  (guest.userId !== "" && guest.userId != null)
                }
                label="Email"
                required
                style={{ width: "300px" }}
                value={guest.email ?? ""}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "email",
                      value: event.currentTarget.value,
                    }),
                  )
                }
              />
            )}
          </Group>
          {guest.primaryGuestId === "" && ventResponse.canUserViewConfig && (
            <TextInput
              disabled={!hasUpdatePermission}
              style={{ width: "500px" }}
              label="Family Greeting"
              mb="sm"
              description="Can use in email greetings, e.g., 'Molly and Family'"
              value={guest.familyGreeting ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "familyGreeting",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
          )}
          <GuestStatusSegmentedControl
            label="Status"
            value={guest.status}
            disabled={!hasUpdatePermission}
            onChange={(value) => {
              if (
                initialData &&
                initialData.status != value &&
                initialData.status === "a" &&
                (guestHasAssignments || guestHasPlusOnes)
              ) {
                if (guestHasAssignments && guestHasPlusOnes) {
                  displayInfoMessage(
                    "The plus ones and activity assignments will NOT be deleted when you change the status from Accepted to Declined or Undecided." +
                      " Please delete the assignments and plus ones (you can just change the status of the plus ones) unless the intent is to commit to them regardless of the status.",
                  );
                } else if (guestHasAssignments) {
                  displayInfoMessage(
                    "The activity assignments will NOT be deleted when you change the status from Accepted to Declined or Undecided." +
                      " Please delete the assignments unless the intent is to commit to them regardless of the status.",
                  );
                } else {
                  displayInfoMessage(
                    "The status of the plus ones will NOT be changed when you change the status from Accepted to Declined or Undecided." +
                      " Please ensure that the status of the plus ones, and any assignments they have, are correct.",
                  );
                }
              }
              dispatch(
                updateField({
                  field: "status",
                  value: value,
                }),
              );
            }}
          />

          {customFieldElements}
          <Stack mt="sm" gap="xs">
            <LabelWithTooltipHelp
              labelText="Opt out of all emails for this event"
              helpText="Not recommended because you might miss important announcements from the host. This also opts you out of guest emails."
            >
              <Switch
                disabled={!hasUpdatePermission || guest.primaryGuestId !== ""}
                label="Opt out of all emails for this event"
                checked={guest.isUnsubscribed}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "isUnsubscribed",
                      value: event.currentTarget.checked,
                    }),
                  )
                }
              />
            </LabelWithTooltipHelp>
            <LabelWithTooltipHelp
              labelText="Opt out of email from other guests (you will still receive emails from the host)"
              helpText="You will not receive emails from other guests if you opt out."
            >
              <Switch
                disabled={!hasUpdatePermission || guest.primaryGuestId !== ""}
                label="Opt out of email from other guests (you will still receive emails from the host)"
                checked={guest.blockedGuestMessaging}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "blockedGuestMessaging",
                      value: event.currentTarget.checked,
                    }),
                  )
                }
              />
            </LabelWithTooltipHelp>
          </Stack>
          {ventResponse.canUserViewConfig && (
            <Textarea
              disabled={!hasUpdatePermission}
              mt="md"
              maxLength={1000}
              label="Host Note (only visible to hosts)"
              value={guest.hostNote ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "hostNote",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
          )}
          <div>
            <Button
              mt="xs"
              mr="md"
              onClick={() => dispatch(reset())}
              disabled={!hasChanges}
            >
              Discard Changes
            </Button>
            <Button
              mt="xs"
              mr="xl"
              onClick={handleSave}
              disabled={!hasChanges || isSavingGuest}
              rightSection={isSavingGuest ? <Loader size={14} /> : null}
            >
              Save RSVP
            </Button>
            {guest.primaryGuestId !== "" && (
              <Anchor
                component={Link}
                href={`/console/guest/event/${ventId}/guest/${guest.primaryGuestId}`}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  Go to the RSVP page of{" "}
                  {formatPrimaryName(
                    guest.primaryGuestId,
                    guest.primaryFirstName,
                    guest.primaryLastName,
                  )}
                </span>
              </Anchor>
            )}
          </div>
        </Fieldset>

        {ventResponse.activities && (
          <Fieldset
            legend={
              `Activities for ${formatFullName(guest.firstName, guest.lastName)} ` +
              (hasChanges ? "(Save RSVP changes to work on activities)" : "")
            }
            mt="sm"
            hidden={
              // Hide activities if user cannot view config (i.e., user is just a guess) unless
              // there are activities.
              !ventResponse.canUserViewConfig && !ventResponse.activities
            }
          >
            <Skeleton visible={isFetchingAssignments} mt="0">
              <Paper
                hidden={
                  isFetchingAssignments === false || plusOneRows.length === 0
                }
              >
                <Table.ScrollContainer minWidth={500}>
                  <Table stickyHeader stickyHeaderOffset={0}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Actions</Table.Th>
                        <Table.Th>Activity</Table.Th>
                        <Table.Th>Comment</Table.Th>
                        <Table.Th>
                          Assigned{" "}
                          {authContext.user?.email === guest.email
                            ? "to You"
                            : "This Guest"}
                        </Table.Th>
                        <Table.Th>Remaining</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{assignmentRows}</Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
              <div hidden={isFetchingAssignments || assignmentRows.length > 0}>
                There are no activities.
              </div>
            </Skeleton>
          </Fieldset>
        )}
        <Fieldset
          legend={
            `Plus Ones for ${formatFullName(guest.firstName, guest.lastName)} ` +
            (hasChanges ? "(Save RSVP changes to work on plus ones)" : "")
          }
          mt="sm"
          hidden={
            // Hide plus ones when looking at a plus one's record because plus ones cannot have plus ones.
            // Also hide if user cannot view config (i.e., user is just a guess) unless
            // guests can have plus ones or guest already has a plus one.
            // An event manager can turn off plus ones after some already exist.
            guest.primaryGuestId !== "" ||
            (!ventResponse.canUserViewConfig &&
              ventResponse.secondaryMax === 0 &&
              plusOneRows.length === 0)
          }
        >
          <Skeleton visible={isFetchingGuest} mt="0">
            <Group>
              <Text size="sm">
                When you add a plus one, you are responsible for their RSVP.
                They will not have access to the event. If they want to manage
                their RSVP, they should sign up on the event home page.
              </Text>
              <Button
                onClick={() => {
                  if (hasChanges) {
                    displayInfoMessage(
                      "You have unsaved RSVP changes. Save or discard them before adding a plus one.",
                    );
                    return;
                  }
                  openPlusOne();
                }}
                disabled={!hasUpdatePermission}
                rightSection={<IconAddNew />}
              >
                Add Plus One
              </Button>
            </Group>
            <Paper hidden={!isSuccessGuest || plusOneRows.length === 0}>
              <Table.ScrollContainer minWidth={500}>
                <Table stickyHeader stickyHeaderOffset={0}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Actions</Table.Th>
                      <Table.Th>Attending</Table.Th>
                      <Table.Th>First Name</Table.Th>
                      <Table.Th>Last Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Activities</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{plusOneRows}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
            <div hidden={isFetchingGuest || plusOneRows.length > 0}>
              There are no plus ones.
            </div>
          </Skeleton>
        </Fieldset>
      </AppShell.Main>
    </>
  );
};

export default GuestTab;
